import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { getStatus, downloadClip } from "../utils/api";

function ResultsPage() {
  useEffect(() => { document.title = "Your Clips · Pixrd"; }, []);
  const { jobId } = useParams();
  const [clips, setClips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [processingStep, setProcessingStep] = useState("");
  const [progress, setProgress] = useState(0);
  const [downloading, setDownloading] = useState(null);
  const [downloadingAll, setDownloadingAll] = useState(false);

  const fetchClips = useCallback(async () => {
    try {
      const data = await getStatus(jobId);

      if (data.status === "completed") {
        setClips(data.clips || []);
        setLoading(false);
      } else if (data.status === "failed") {
        setError(data.error || "Processing failed");
        setLoading(false);
      } else {
        // update loading message with current step
        setProcessingStep(data.step || "Processing...");
        setProgress(data.progress || 0);

        // Keep polling
        setTimeout(fetchClips, 2000);
      }
    } catch (err) {
      // Network error - keep trying
      console.log("Network error, retrying...", err);
      setTimeout(fetchClips, 3000);
    }
  }, [jobId]);

  useEffect(() => {
    fetchClips();
  }, [fetchClips]);

  const handleDownload = async (filename) => {
    // Download individual clip
    setDownloading(filename);
    await downloadClip(jobId, filename);
    setDownloading(null);
  };

  const handleDownloadAll = async () => {
    // Download all clips
    setDownloadingAll(true);
    for (const clip of clips) {
        await downloadClip(jobId, clip.filename);
        await new Promise(r => setTimeout(r, 500));
    }
    setDownloading(null);
    setDownloadingAll(false);
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen bg-bg-primary pt-[120px] pb-20 px-6 flex items-center justify-center">
          <div className="text-center">
            <div
              className="w-16 h-16 border-4 rounded-full mx-auto mb-4 animate-spin-fast"
              style={{
                borderColor: "#888888",
                borderTopColor: "#FF6B00",
              }}
            ></div>
            <p
              className="font-space text-[18px] mb-2"
              style={{ color: "#FF6B00", fontWeight: "600" }}
            >
              {processingStep || "Loading clips..."}
            </p>
            {progress > 0 && (
              <p
                className="font-space text-[14px]"
                style={{ color: "#888888", fontWeight: "500" }}
              >
                {progress}%
              </p>
            )}
          </div>
        </div>
        <Footer />
      </>
    );
  }

  if (error) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen bg-bg-primary pt-[120px] pb-20 px-6">
          <div className="max-w-3xl mx-auto text-center">
            <p
              className="font-space text-[18px] p-6 border rounded"
              style={{
                color: "#FF6B00",
                borderColor: "#FF6B00",
                fontWeight: "600",
              }}
            >
              {error}
            </p>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-bg-primary pt-[120px] pb-20 px-6">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h1
              className="font-space text-[clamp(28px,4vw,40px)] mb-2"
              style={{
                color: "#FF6B00",
                fontWeight: "700",
              }}
            >
              {clips.length} Clips Ready!
            </h1>
          </div>

          {/* Clips List */}
          <div className="space-y-8 mb-12">
            {clips.map((clip, index) => (
              <div
                key={clip.id || index}
                className="border rounded-lg p-6"
                style={{ borderColor: "#888888" }}
              >
                {/* Clip Header */}
                <div className="flex justify-between items-start mb-4">
                  <h3
                    className="font-space text-[18px]"
                    style={{ color: "#b0b0b0", fontWeight: "600" }}
                  >
                    CLIP {index + 1}
                  </h3>
                  <div className="text-right">
                    <p
                      className="font-space text-[14px]"
                      style={{ color: "#888888", fontWeight: "500" }}
                    >
                      score: {clip.score}/10
                    </p>
                  </div>
                </div>

                {/* Video Player */}
                <div className="mb-4 rounded-lg overflow-hidden">
                  <video
                    className="w-full"
                    controls
                    preload="metadata"
                    style={{ backgroundColor: "#000000" }}
                  >
                    <source src={clip.url} type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                </div>

                {/* Suggested Hook */}
                <div className="mb-3">
                  <p
                    className="font-space text-[12px] mb-1"
                    style={{ color: "#777777", fontWeight: "600" }}
                  >
                    SUGGESTED HOOK:
                  </p>
                  <p
                    className="font-space text-[14px]"
                    style={{ color: "#FF6B00", fontWeight: "600" }}
                  >
                    "{clip.suggested_hook}"
                  </p>
                </div>

                {/* Duration */}
                <div className="mb-3">
                  <p
                    className="font-space text-[14px]"
                    style={{ color: "#888888", fontWeight: "500" }}
                  >
                    Duration: {clip.duration}s
                  </p>
                </div>

                {/* AI Reason */}
                <div className="mb-4">
                  <p
                    className="font-space text-[12px] mb-1"
                    style={{ color: "#777777", fontWeight: "600" }}
                  >
                    WHY AI CHOSE THIS:
                  </p>
                  <p
                    className="font-space text-[14px]"
                    style={{ color: "#b0b0b0", fontWeight: "500" }}
                  >
                    {clip.reason}
                  </p>
                </div>

                {/* Download Button */}
                <button
                  onClick={() => handleDownload(clip.filename)}
                  className="w-full font-space text-[12px] tracking-[3px] uppercase py-3 border transition-all duration-200 rounded"
                  style={{
                    borderColor: "#888888",
                    color: "#b0b0b0",
                    fontWeight: "700",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.borderColor = "#FF6B00";
                    e.target.style.color = "#FF6B00";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.borderColor = "#888888";
                    e.target.style.color = "#b0b0b0";
                  }}
                >
                  {downloading === clip.filename ? "Downloading..." : "Download Clip"}
                </button>
              </div>
            ))}
          </div>

          {/* Download All Button */}
          <button
            onClick={handleDownloadAll}
            className="w-full font-space text-[14px] tracking-[3px] uppercase py-4 transition-all duration-200 rounded"
            style={{
              backgroundColor: "#FF6B00",
              color: "#000000",
              fontWeight: "700",
            }}
            onMouseEnter={(e) => (e.target.style.backgroundColor = "#FF9A3C")}
            onMouseLeave={(e) => (e.target.style.backgroundColor = "#FF6B00")}
          >
            {downloadingAll ? "Downloading All..." : "Download All Clips"}
          </button>
        </div>
      </div>
      <Footer />
    </>
  );
}

export default ResultsPage;
